<template>
  <v-card class="fill-height fluid">
    <v-toolbar dark color="primary darken-1">
      <v-toolbar-title>Defect{{ defectNumber ? ` ${defectNumber}` : '' }} Log</v-toolbar-title>
    </v-toolbar>
    <v-card-text>
      <div v-if="$wait.is('loading-audit-records')" class="text-center pa-4">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        ></v-progress-circular>
        <div class="mt-2">Loading audit records...</div>
      </div>
      <v-expansion-panels v-else-if="audits && audits.length > 0" multiple>
        <v-expansion-panel v-for="audit in audits" :color="audit.color" :key="audit.id">
          <v-expansion-panel-header>
            <template v-slot:default="{ open }">
              <v-row no-gutters>
                <v-col cols="1">
                  <v-icon :color="audit.color">
                    {{ audit.icon }}
                  </v-icon>
                </v-col>
                <v-col cols="5">
                  <strong>{{ audit.title }}</strong
                  ><br />
                </v-col>
                <v-col cols="5">
                  <div align="right">
                    <small>
                      <template v-if="audit.date">
                        {{ moment(audit.date).local().format('D MMM YYYY hh:mma') }}<br />
                      </template>
                      {{ audit.username }}
                    </small>
                  </div>
                </v-col>
              </v-row>
            </template>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <ul v-for="change in audit.detail">
              <li v-if="change.propertyName.toLowerCase() === 'work session'">
                <strong>{{ audit.username }}</strong> completed
                <v-chip class="wrap-chip" small color="green" outlined label
                  >{{ moment.duration(change.newValue, 'seconds').hours() }}h {{ moment.duration(change.newValue, 'seconds').minutes() }}m {{ moment.duration(change.newValue, 'seconds').seconds() }}s</v-chip
                >
                of work
              </li>
              <li v-else-if="change.propertyName.toLowerCase() === 'hivis' && change.newValue">
                <strong>HIVIS</strong> acknowledged at <v-chip class="wrap-chip" small color="green" outlined label>{{ change.newValue }}</v-chip>
              </li>
              <li v-else-if="change.propertyName.toLowerCase() === 'hivis' && !change.newValue">
                <v-icon color="red">warning</v-icon> <strong>HIVIS</strong> was not acknowledged
              </li>
              <li v-else-if="change.propertyName.toLowerCase() === 'accepted' && change.newValue">
                <strong>Accepted</strong> at <v-chip class="wrap-chip" small color="success" outlined label>{{ change.newValue }}</v-chip>
              </li>
              <li v-else-if="change.oldValue && change.newValue">
                Changed <strong>{{ change.propertyName }}</strong> from <v-chip class="wrap-chip" small color="orange" outlined label>{{ change.oldValue }}</v-chip> to
                <v-chip class="wrap-chip" small color="green" outlined label>{{ change.newValue }}</v-chip>
              </li>
              <li v-else-if="change.oldValue && !change.newValue">
                Removed <v-chip class="wrap-chip" small color="orange" outlined label>{{ change.oldValue }}</v-chip> from <strong>{{ change.propertyName }}</strong>
              </li>
              <li v-else-if="change.propertyName.toLowerCase() === 'started' && change.newValue">
                <strong>Started</strong> at <v-chip class="wrap-chip" small color="green" outlined label>{{ change.newValue }}</v-chip>
              </li>
              <li v-else-if="change.propertyName.toLowerCase() === 'stopped' && change.newValue">
                <strong>Stopped</strong> at <v-chip class="wrap-chip" small color="green" outlined label>{{ change.newValue }}</v-chip>
              </li>
              <li v-else-if="!change.oldValue && change.newValue">
                Set <strong>{{ change.propertyName }}</strong> to <v-chip class="wrap-chip" small color="green" outlined label>{{ change.newValue }}</v-chip>
              </li>
            </ul>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <div v-else>No audit records found.</div>
    </v-card-text>
  </v-card>
</template>

<script>
import Audits from '@/models/IDefectNotice.ts';
import moment from 'moment/moment';

export default {
  mixins: [Audits],
  props: {
    defectId: {
      type: String,
      required: true
    },
    defectNumber: {
      type: Number,
      default: null
    },
    workSessions: {
      type: Array,
      default: () => []
      // Each session has: id, hivisConfirmationTimestamp, startedTimestamp, stoppedTimestamp, totalSecondsWorked, isActive, user
    },
    assignees: {
      type: Array,
      default: () => []
      // Each assignee has: id, displayName, acceptedAt
    },
    historicalAcceptedJobs: {
      type: Array,
      default: () => []
      // Soft-deleted assignees that accepted the job: id, displayName, acceptedAt
    }
  },
  data() {
    return {
      audits: []
    };
  },
  methods: {
    async loadAudits() {
      try {
        this.$wait.start('loading-audit-records');

        let audits = [];
        let workSessions = [];
        let acceptedJobs = [];

        // only fleet managers should see the audit records
        if (this.userIsFleetManager) {
          let response = await this.$api.get(`audits?types=GateKeeper.Domain.Defect,GateKeeper.Domain.PrecompletedDefect&entityId=${this.defectId}`);
          audits = response.data.data.map(audit => ({
            id: audit.id,
            username: audit.username,
            date: moment(audit.dateChanged),
            color: 'blue',
            icon: 'mdi-clipboard-edit',
            title: generateAuditTitle(audit),
            detail: audit.propertyChanges
          }));
        }

        if (this.workSessions) {
          workSessions = this.workSessions.map(session => ({
            id: session.id,
            username: session.user,
            // Use stoppedTimestamp for completed sessions, or startedTimestamp for active, or hivisConfirmationTimestamp as fallback
            // If all timestamps are null (legacy data), set date to null to avoid moment.utc() returning current time
            date: (session.stoppedTimestamp || session.startedTimestamp || session.hivisConfirmationTimestamp)
              ? moment.utc(session.stoppedTimestamp || session.startedTimestamp || session.hivisConfirmationTimestamp)
              : null,
            color: 'green',
            icon: session.isActive ? 'mdi-play-circle' : 'mdi-clipboard-clock',
            title: session.isActive ? 'Work Session (Active)' : (session.stoppedTimestamp ? 'Work Session Completed' : 'Work Session'),
            detail: generateWorkSessionDescription(session)
          }));
        }

        if (this.assignees) {
          acceptedJobs = this.assignees
            .filter(a => a && typeof a === 'object' && a.acceptedAt)
            .map(a => ({
              id: `accepted-${a.id}`,
              username: a.displayName,
              date: moment.utc(a.acceptedAt),
              color: 'success',
              icon: 'mdi-check-circle',
              title: 'Job Accepted',
              detail: [{
                propertyName: 'Accepted',
                newValue: moment.utc(a.acceptedAt).local().format('D MMM YYYY h:mma')
              }]
            }));
        }

        if (this.historicalAcceptedJobs) {
          const historicalJobs = this.historicalAcceptedJobs
            .filter(a => a && typeof a === 'object' && a.acceptedAt)
            .map(a => ({
              id: `accepted-hist-${a.id}-${a.acceptedAt}`,
              username: a.displayName,
              date: moment.utc(a.acceptedAt),
              color: 'success',
              icon: 'mdi-check-circle',
              title: 'Job Accepted',
              detail: [{
                propertyName: 'Accepted',
                newValue: moment.utc(a.acceptedAt).local().format('D MMM YYYY h:mma')
              }]
            }));
          acceptedJobs = [...acceptedJobs, ...historicalJobs];
        }

        this.audits = [...audits, ...workSessions, ...acceptedJobs];

        this.audits.sort((a, b) => {
          // Put entries without dates at the top
          if (!a.date && !b.date) return 0;
          if (!a.date) return -1;
          if (!b.date) return 1;
          return new Date(b.date) - new Date(a.date);
        });

        function generateAuditTitle(audit) {
          if (audit.action.toLowerCase() === 'added') {
            return audit.entityName.split('.')[audit.entityName.split('.').length - 1] + ' Created';
          } else if (audit.propertyChanges.some(pc => pc.propertyName.toLowerCase() === 'status' && pc.newValue.toLowerCase() === 'received')) {
            return audit.entityName.split('.')[audit.entityName.split('.').length - 1] + ' Received';
          } else if (audit.propertyChanges.some(pc => pc.propertyName.toLowerCase() === 'status' && pc.newValue.toLowerCase() === 'completedpendingreview')) {
            return audit.entityName.split('.')[audit.entityName.split('.').length - 1] + ' Completed';
          } else {
            return audit.entityName.split('.')[audit.entityName.split('.').length - 1] + ' ' + audit.action.toLowerCase();
          }
        }

        function generateWorkSessionDescription(session) {
          const details = [];

          // Show duration for completed sessions
          if (session.totalSecondsWorked > 0) {
            details.push({
              propertyName: 'work session',
              newValue: session.totalSecondsWorked
            });
          }

          // Show HIVIS status
          details.push({
            propertyName: 'HIVIS',
            newValue: session.hivisConfirmationTimestamp
              ? moment.utc(session.hivisConfirmationTimestamp).local().format('D MMM YYYY h:mma')
              : null
          });

          // Show start time
          if (session.startedTimestamp) {
            details.push({
              propertyName: 'Started',
              newValue: moment.utc(session.startedTimestamp).local().format('D MMM YYYY h:mma')
            });
          }

          // Show stop time for completed sessions
          if (session.stoppedTimestamp) {
            details.push({
              propertyName: 'Stopped',
              newValue: moment.utc(session.stoppedTimestamp).local().format('D MMM YYYY h:mma')
            });
          }

          return details;
        }
      } catch (error) {
        this.$toast.error('There was an error loading defect audit records');
        this.audits = [];
      } finally {
        this.$wait.end('loading-audit-records');
      }
    }
  },
  watch: {
    defectId: {
      immediate: true,
      handler(val) {
        if (val) {
          this.loadAudits();
        }
      }
    }
  },
  beforeDestroy() {
    // Clear audits when component is destroyed (dialog closes or panel is hidden)
    this.audits = [];
  }
};
</script>

<style scoped>
.wrap-chip {
  height: auto !important;
  min-height: 24px; /* keep small chip baseline */
  max-width: 100%;
  white-space: normal; /* allow wrapping if parent styles apply */
}
.wrap-chip .v-chip__content {
  white-space: normal; /* enable text wrapping inside chip */
  word-break: break-word; /* break long words/IDs */
  overflow-wrap: anywhere; /* modern fallback for long tokens */
}
</style>
